import { Component, EventEmitter, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { of } from 'rxjs';
import { switchMap, tap, distinctUntilChanged, map, filter } from 'rxjs/operators';

import { MoteEventType, Mote, GroupEventType, GroupMemberState, GroupEvent, Media, Group, MoteEvent, GroupMember, Challenge, DatabaseModel, MediaState, UnlockMoteEvent } from '../../models/';
import { GroupService, AutoSub, AutoSubOnDestroy, MoteEventService, VoucherService, MoteService, MediaService, QuizService, ProjectService } from '../../providers/';
import { MotesMediaPlayerService } from '../../motes-media-player/media-player.service';

/**
 * The available types of group popups
 */
export enum GroupPopup {
    NONE = "NONE",
    START_TOUR_SELECT = "START_TOUR_SELECT",
    START_TOUR_DECLINED = "START_TOUR_DECLINED",
    START_TOUR_ACCESS_DENIED = "START_TOUR_ACCESS_DENIED",
    PLAYBACK_REQUEST = "PLAYBACK_REQUEST",
    QUIZ_REQUEST = "QUIZ_REQUEST",
    QUIZ_SCOREBOARD = "QUIZ_SCOREBOARD",
    PREVENT_REQUEST = "PREVENT_REQUEST"
}

/**
 * Manage and display group popups based on group events
 */
@AutoSubOnDestroy()
@Component({
    selector: 'group-popups',
    templateUrl: './group-popups.component.html',
    styleUrls: ['./group-popups.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class GroupPopupsComponent implements OnInit {
    /** The current group */
    public group: Group = null;
    /** Members of the current group */
    public members: GroupMember[] = null;
    /** The group member who initiated the current event */
    public initiatingMember: GroupMember = null;
    /** The name of the group member who initiated the current event. Alternatively, a generic localized string */
    public initiatingMemberName: string = null;
    /** The current group popup */
    public popup: GroupPopup = GroupPopup.NONE;
    /** Show a dialog to confirm the user's intention of skipping the current group request */
    public showSkipConfirmation = false;
    /** The current group event */
    public event: GroupEvent = null;
    /** The `Mote` associated with the current group event */
    public mote: Mote = null;
    /** The `Media` associated with the current group event */
    public media: Media = null;
    /** The `Challenge` associated with the current group event */
    public challenge: Challenge = null;
    /** Number of questions/possible answers in quiz */
    public maxQuizQuestions: number = 0;
    /** Key for tour (mote) the user does not have access to, if any */
    private restrictedTourKey: string = null;
    /** The join ID for the group */
    public groupJoinId: number = null;

    /** Emits a mote key. This event fires when the user does not have access to a tour and wants to browse tour details. */
    @Output() browseTourEvent = new EventEmitter<string>();

    constructor(
        public groupService: GroupService,
        private moteService: MoteService,
        private eventService: MoteEventService,
        private voucherService: VoucherService,
        private mediaService: MediaService,
        private quizService: QuizService,
        private mediaPlayerService: MotesMediaPlayerService,
        private projectService: ProjectService
    ) { }

    ngOnInit(): void {
        // Watch for group switching. If a new group is detected, check if it has an active tour
        AutoSub(this).reg['groupSwitching'] = this.groupService.watchCurrentGroup().pipe(
            // Get the group key
            map((group) => group?.key),
            distinctUntilChanged(),
            // Filter out falsy values
            filter<string>(Boolean),
            // Get the latest GROUP_START_TOUR event (and forward it to the handler function)
            switchMap((groupKey) => this.groupService.getGroupEvent(groupKey, GroupEventType.GROUP_START_TOUR)))
            .subscribe((event) => this.handleGroupEvent(event));

        // Watch group events as they occur and forward to handler function
        AutoSub(this).reg['groupAndGroupEvents'] = this.groupService.watchCurrentGroup().pipe(
            // Store group in member variable
            tap((group) => this.group = group),
            // Convert group members from mapped type to array
            tap((group) => this.members = DatabaseModel.mapToArray(group?.members)),
            // Watch group events
            switchMap((group) => group ? this.groupService.watchGroupEvents(group.key) : of(null)))
            // Forward to handler function
            .subscribe((event) => this.handleGroupEvent(event));

        // Watch mote events as they occur and forward to handler function
        AutoSub(this).reg['moteEvents'] = this.eventService.watchMoteEvents()
            .subscribe((event) => this.handleMoteEvent(event));
    }

    /**
     * Handle incoming group events
     * @param event The event to handle
     */
    private async handleGroupEvent(event: GroupEvent): Promise<void> {
        // Get the group member associated with the current user
        // If it does not exist locally, get it from the database
        const currentMember = this.groupService.getCurrentMember() || await this.groupService.getDbMember();

        // If there is no event or group member, do nothing
        if(!(event && currentMember)) return;
        // If the event was emitted by this user, do nothing, unless it is a playback or quiz request
        if(event.userKey == currentMember.key && !(event.type == GroupEventType.GROUP_PLAYBACK_REQUEST || event.type == GroupEventType.GROUP_QUIZ_REQUEST)) return;

        // Save event, mote, media and the initiating group member in member variables
        this.event = event;
        const eventMote = await this.moteService.getMote(event.moteKey);
        this.mote = eventMote;

        const eventMedia = await this.mediaService.getMedia(event.mediaKey);
        this.media = eventMedia;
        this.initiatingMember = this.members?.find((member) => member.key == event.userKey);
        this.initiatingMemberName = this.initiatingMember ? this.initiatingMember.userName : $localize`Someone in your group`;

        switch(event.type) {
            case GroupEventType.GROUP_START_TOUR:
                // If the tour is already active, do nothing
                if(this.projectService.activeProject?.activeRouteKey == event.moteKey) break;
                // Get tour (include preview from event to make sure user have access)
                const tour = await this.moteService.getMotePreview(event.moteKey, event.previewKey);
                // Check whether the user has access to the group tour
                const userHasTourAccess = await this.voucherService.verifyUserTourAccess(currentMember.key, tour);
                // Store joinId for the current group in member variable (in order to use the data when displaying popups)
                this.groupJoinId = (await this.groupService.getCurrentGroup())?.joinId;

                // If the user has access to the tour, let them select whether to join or not
                if(userHasTourAccess) {
                    this.popup = GroupPopup.START_TOUR_SELECT;
                    // Else, display popup with info about getting access
                } else {
                    // Save the key for the restricted tour (used for navigating to the tour page)
                    this.restrictedTourKey = eventMote.key;
                    this.groupService.leaveGroup();
                    this.popup = GroupPopup.START_TOUR_ACCESS_DENIED;
                }
                break;
            case GroupEventType.GROUP_UNLOCK_MOTE:
                // If there is no event mote, do nothing
                if(!eventMote) break;
                // If the user is already viewing the story associated with the event, do nothing
                if(eventMote.key == this.getOpenStoryKey()) break;
                // If the current user is not available, simply unlock the mote in the background
                if(!this.groupService.isCurrentMemberAvailable()) {
                    let unlockEvent: UnlockMoteEvent = { type: MoteEventType.UNLOCK_DONE, mote: eventMote, options: { preventViewChange: true } };
                    this.eventService.emitMoteEvent(unlockEvent);
                    break;
                }
                // Otherwise, emit a GROUP_UNLOCK_MOTE MoteEvent (which will trigger animations etc.)
                let moteEvent = {
                    type: MoteEventType.GROUP_UNLOCK_MOTE,
                    mote: eventMote
                };
                // Emit event
                this.eventService.emitMoteEvent(moteEvent);
                break;
            case GroupEventType.GROUP_PLAYBACK_REQUEST:
                // Set the state of the group member to WAITING and display the PLAYBACK_REQUEST popup
                await this.groupService.setCurrentMemberState(this.group.key, GroupMemberState.WAITING);
                // Display the popup and pause the media player (if it's playing)
                this.popup = GroupPopup.PLAYBACK_REQUEST;
                if(this.mediaPlayerService.getCurrentState() == MediaState.PLAYING) this.mediaPlayerService.pausePlayer();
                break;
            case GroupEventType.GROUP_QUIZ_REQUEST:
                // Get all motes (/questions) associated with the quiz (get children is cached so should be ok fast)
                const children = await this.moteService.getChildren(eventMote.getParent());
                this.maxQuizQuestions = children.filter(mote => mote.quiz.challenge == eventMote.quiz.challenge).length;
                // Prevent the group member from answering the same quiz question twice:
                // Get the group member corresponding to the current user
                const groupMember = this.members?.find((member) => member.key == currentMember?.key);
                // Get the group members `ChallengeState`
                const challengeState = groupMember?.challengeStateMap.getChallengeState(eventMote?.quiz.challenge);
                // If an entry for the current mote key exists in the group members `ChallengeState.pointMap`, the question has already been answered
                if(challengeState.correctAnswersMap[event.moteKey]) break;

                // Set the state of the group member to WAITING and display the QUIZ_REQUESTED popup    
                await this.groupService.setCurrentMemberState(this.group.key, GroupMemberState.WAITING);
                // Display the popup and pause the media player (if it's playing)
                this.popup = GroupPopup.QUIZ_REQUEST;
                if(this.mediaPlayerService.getCurrentState() == MediaState.PLAYING) this.mediaPlayerService.pausePlayer();
                break;
        }
    }

    /**
     * Handle incoming mote events
     * @param event The event to handle
     */
    private async handleMoteEvent(event: MoteEvent): Promise<void> {
        switch(event.type) {
            case MoteEventType.QUIZ_SKIP_REQUESTED:
                this.mote = event.mote;
                this.showSkipConfirmation = true;
                break;
            case MoteEventType.QUIZ_COMPLETED:
                if(!this.group) break;
                this.challenge = await this.quizService.getChallenge(event.mote.quiz.challenge);
                this.mote = event.mote;
                this.popup = GroupPopup.QUIZ_SCOREBOARD;
                break;
            case MoteEventType.GROUP_PREVENT_REQUEST:
                this.popup = GroupPopup.PREVENT_REQUEST;
                break;
        }
    }

    public joinGroupTour() {
        // Create GROUP_START_TOUR MoteEvent with the group tour
        let moteEvent = {
            type: MoteEventType.GROUP_START_TOUR,
            mote: this.mote
        };
        // Emit event
        this.eventService.emitMoteEvent(moteEvent);
        // Reset state
        this.resetState();
    }

    public declineGroupTour(): void {
        this.groupService.leaveGroup();
        this.popup = GroupPopup.START_TOUR_DECLINED;
    }

    public goToTourDetails() {
        // Emit browseTourEvent with key for the tour the user does not have access to
        this.browseTourEvent.emit(this.restrictedTourKey);
        // Reset state
        this.resetState();
    }

    /**
     * Request skipping a quiz question
     */
    public emitQuizSkipRequest(): void {
        // Emit `QUIZ_SKIP_REQUESTED` event
        const event = { type: MoteEventType.QUIZ_SKIP_REQUESTED, mote: this.mote };
        this.eventService.emitMoteEvent(event);
    }

    /**
     * Mark the current group member `READY` for synchronous playback
     */
    public async markMemberReadyForPlayback(): Promise<void> {
        // Set up media player
        await this.mediaPlayerService.preparePlayer(this.mote, this.media);
        // Set group member state to READY
        this.groupService.setCurrentMemberState(this.group.key, GroupMemberState.READY);
    }

    /**
     * Mark the current group member `READY` for quiz
     */
    public markMemberReadyForQuiz(): void {
        // Set the group member state to READY
        this.groupService.setCurrentMemberState(this.group.key, GroupMemberState.READY);
    }

    /**
     * Start the media player and set group member state to `IDLE`
     */
    public startPlayback(): void {
        // Perform the following tasks asynchronously to prevent `ExpressionChangedAfterItHasBeenCheckedError`
        // Source: https://angular.io/errors/NG0100
        Promise.resolve().then(() => {
            // Start media player
            this.mediaPlayerService.startPlayer(this.mote, this.media);
            // Reset state of the component
            this.resetState();
        });
        // Set group member state to IDLE
        this.groupService.setCurrentMemberState(this.group.key, GroupMemberState.IDLE);
    }

    /**
     * Emit a `QUIZ_STARTED` event and set group member state to `ACTIVE`
     */
    public startQuiz(): void {
        let event = {
            type: MoteEventType.QUIZ_STARTED,
            mote: this.mote
        };
        // Emit the event
        this.eventService.emitMoteEvent(event);

        // Set `GroupMemberState` of members who are now answering a quiz question to `ACTIVE` (other members should be `IDLE`)
        this.groupService.setCurrentMemberState(this.group.key, GroupMemberState.ACTIVE);

        // Perform the following tasks asynchronously to prevent `ExpressionChangedAfterItHasBeenCheckedError`
        // Source: https://angular.io/errors/NG0100
        Promise.resolve().then(() => {
            // Reset state of the component
            this.resetState();
        });
    }

    /**
     * Emit `QUIZ_SCOREBOARD_CLOSED` event and reset component state
     */
    public emitQuizScoreboardClosed(): void {
        const event = { type: MoteEventType.QUIZ_SCOREBOARD_CLOSED, mote: this.mote };
        this.eventService.emitMoteEvent(event);
        this.resetState();
    }

    /**
     * Skip synchronous media playback
     */
    public skipPlayback(): void {
        // Reset state of the component
        this.resetState();
        // Stop the media player
        this.mediaPlayerService.stopPlayer();
        // Set group member state to IDLE
        this.groupService.setCurrentMemberState(this.group.key, GroupMemberState.IDLE);
    }

    /**
     * Skip a quiz question
     */
    public skipQuiz(): void {
        // Emit `QUIZ_SKIPPED` event
        const event = { type: MoteEventType.QUIZ_SKIPPED, mote: this.mote };
        this.eventService.emitMoteEvent(event);
        // Reset state of the component
        this.resetState();
    }

    public resetState(): void {
        this.popup = GroupPopup.NONE;
        this.mote = null;
        this.groupJoinId = null;
        this.initiatingMember = null;
        this.initiatingMemberName = null;
        this.showSkipConfirmation = false;
    }

    /**
     * Get the key for the currently open story
     * @returns The key for the currently open story or `undefined` if no story is open.
     */
    private getOpenStoryKey(): string | undefined {
        // Get the current URL path
        const currentUrlPath = location.pathname;
        // Match any path segment between `/story/` and a subsequent `/`. Uses a named capture group, `storyKey`
        const storyKeyMatcher = /\/story\/(?<storyKey>[^\/]*)/;
        return currentUrlPath.match(storyKeyMatcher)?.groups?.storyKey;
    }
}
